import Axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Table, Button, message, Select, Modal, Tabs, Spin } from 'antd';
import ExcelJS from 'exceljs';
import { surveyCode } from '../../Config/surveyCode';
// テーブル強化 PJ Ph.3 拡大: メイン一覧テーブルに 列リサイズ + ソート + 省略
// （設問プレビューのモーダルテーブルは従来の <Column> のまま）
import {
  PageContainer,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
} from '../../common';

const { Column } = Table;
const { Option } = Select;

const SeasonListPage = (props) => {
  const { user } = props;
  const [seasonInfo, setSeasonInfo] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selGroupCode, setSelGroupCode] = useState(null);
  const [filteredSeasons, setFilteredSeasons] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [questionData, setQuestionData] = useState({});
  const [modalSeason, setModalSeason] = useState(null);

  const readDB = async () => {
    try {
      if (!user.userData) return null;
      if (user.userData.isAuth) {
        const [seasonRes, groupRes] = await Promise.all([
          Axios.get('/api/surveySeason/'),
          Axios.get('/api/group/'),
        ]);

        if (seasonRes.data.success) {
          let ssArr = seasonRes.data.surveySeason;
          ssArr.map((element) => {
            element.survey_name = surveyCode[element.survey_code];
            return element;
          });
          setSeasonInfo(ssArr);
          setFilteredSeasons(ssArr);
        } else {
          throw new Error('シーズン取得に失敗しました。');
        }

        if (groupRes.data.success) {
          if (
            user.userData.groupCode === 'RECRNT' &&
            user.userData.role === 2
          ) {
            const nonGroup = groupRes.data.groups.filter(
              (item) => item.group_code !== 'RECRNT'
            );
            setGroups(nonGroup);
          } else {
            setGroups(groupRes.data.groups);
          }
        }
      } else {
        throw new Error('ログイン前です。');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    readDB();
  }, [user]);

  // 個別Question直接割当タイプ（QuestionSet不要のサーベイ）
  const DIRECT_QUESTION_CODES = [
    '10010', '30010', '50010', '50020', '50030', '50040',
    '60010', '70010', '80010',
    '90101', '90301', '90501', '90502', '90503', '90601',
  ];
  // QuestionSet割当が必要なサーベイ
  const NEEDS_QUESTION_SET_CODES = ['20110', '20210', '40010', '60020'];
  // QuestionSetが2個必要なサーベイ（本人用・協力者用）
  const NEEDS_TWO_QUESTION_SETS = ['40010', '60020'];

  const onChangeGroup = (value) => {
    setSelGroupCode(value);
    if (value) {
      setFilteredSeasons(seasonInfo.filter((s) => s.group_code === value));
    } else {
      setFilteredSeasons(seasonInfo);
    }
  };

  const showQuestionModal = async (record) => {
    setModalVisible(true);
    setModalLoading(true);
    setQuestionData({});
    setModalSeason(record);

    try {
      if (record.question_set && record.question_set.length > 0) {
        const allIds = [];
        record.question_set.forEach((qs) => {
          if (qs.question_id && qs.question_id.length > 0) {
            qs.question_id.forEach((id) => {
              if (!allIds.includes(id)) allIds.push(id);
            });
          }
        });

        if (allIds.length > 0) {
          const res = await Axios.post('/api/question/byIds', { ids: allIds });
          if (res.data.success) {
            const questionsMap = {};
            res.data.questions.forEach((q) => {
              questionsMap[q._id] = q;
            });

            const grouped = {};
            record.question_set.forEach((qs) => {
              const setName = qs.set_name || '設問セット';
              const setQuestions = (qs.question_id || [])
                .map((id) => questionsMap[id])
                .filter(Boolean)
                .sort((a, b) => Number(a.no) - Number(b.no));
              grouped[setName] = setQuestions;
            });
            setQuestionData(grouped);
          }
        }
      } else {
        const res = await Axios.get(`/api/question/${record.survey_code}`);
        if (res.data.success) {
          setQuestionData({ '設問一覧': res.data.question });
        }
      }
    } catch (error) {
      console.log(error);
      message.error('設問データの取得に失敗しました。');
    } finally {
      setModalLoading(false);
    }
  };

  const sanitizeSheetName = (name) => {
    // Excelシート名の禁則文字を除去（ : \ / ? * [ ]）と31文字制限
    const cleaned = String(name).replace(/[\\\/\?\*\[\]:]/g, '_');
    return cleaned.length > 31 ? cleaned.slice(0, 31) : cleaned;
  };

  const downloadExcel = async () => {
    try {
      const keys = Object.keys(questionData);
      if (keys.length === 0) {
        message.warning('ダウンロードする設問がありません。');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const usedNames = new Set();

      keys.forEach((key, idx) => {
        // シート名のユニーク化
        let baseName = sanitizeSheetName(key) || `Sheet${idx + 1}`;
        let sheetName = baseName;
        let suffix = 1;
        while (usedNames.has(sheetName)) {
          const trimLen = 31 - String(suffix).length - 1;
          sheetName = `${baseName.slice(0, trimLen)}_${suffix}`;
          suffix += 1;
        }
        usedNames.add(sheetName);

        const sheet = workbook.addWorksheet(sheetName);
        sheet.columns = [
          { header: 'No', key: 'no', width: 8 },
          { header: '設問', key: 'question', width: 60 },
          { header: '選択肢', key: 'choices', width: 60 },
        ];

        const questions = questionData[key] || [];
        questions.forEach((q) => {
          let choicesText = '-';
          if (q.type === '20') {
            choicesText = '自由記述';
          } else if (q.choices && q.choices.length > 0) {
            choicesText = q.choices
              .map((c, i) => `${i + 1}. ${c}`)
              .join('\n');
          }
          const row = sheet.addRow({
            no: q.no,
            question: q.question,
            choices: choicesText,
          });
          row.getCell('question').alignment = { wrapText: true, vertical: 'top' };
          row.getCell('choices').alignment = { wrapText: true, vertical: 'top' };
        });

        sheet.getRow(1).font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `設問一覧_${
        (modalSeason && modalSeason.season_name) || 'survey'
      }.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      message.error('Excelダウンロードに失敗しました。');
    }
  };

  const renderQuestionTable = (questions) => (
    <Table
      dataSource={questions}
      size="small"
      rowKey="_id"
      pagination={false}
      scroll={{ y: 500 }}
    >
      <Column title="No" dataIndex="no" key="no" width={60} />
      <Column title="設問" dataIndex="question" key="question" />
      <Column
        title="選択肢"
        key="choices"
        width={300}
        render={(text, record) => {
          if (record.type === '20') return '自由記述';
          if (record.choices && record.choices.length > 0) {
            return record.choices
              .map((c, i) => `${i + 1}. ${c}`)
              .join(' / ');
          }
          return '-';
        }}
      />
    </Table>
  );

  const editSeasonHandler = async (sInfo, e) => {
    e.preventDefault();
    try {
      props.history.push({
        pathname: '/makeseason',
        state: sInfo,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const newSeasonHandler = async (e) => {
    e.preventDefault();
    try {
      props.history.push({
        pathname: '/makeseason',
        state: {},
      });
    } catch (error) {
      console.log(error);
    }
  };

  const tabKeys = Object.keys(questionData);

  // テーブル強化 PJ Ph.3 拡大: メイン一覧の <Column> → columns 配列。テキスト列はソート + 省略、
  // 割当済み・編集列は既存ボタンを維持。
  const rawColumns = [
    ellipsisColumn({
      title: 'サーベイ・シーズン名',
      dataIndex: 'season_name',
      key: 'season_name',
      width: 240,
      sorter: textSorter('season_name'),
    }),
    ellipsisColumn({
      title: 'シーズン説明',
      dataIndex: 'season_description',
      key: 'season_description',
      width: 240,
      sorter: textSorter('season_description'),
    }),
    ellipsisColumn({
      title: 'グループコード',
      dataIndex: 'group_code',
      key: 'group_code',
      width: 160,
      sorter: textSorter('group_code'),
    }),
    ellipsisColumn({
      title: 'サーベイ種類',
      dataIndex: 'survey_name',
      key: 'survey_code',
      width: 180,
      sorter: textSorter('survey_name'),
    }),
    ellipsisColumn({
      title: '研修名',
      dataIndex: 't_name',
      key: 't_name',
      width: 180,
      sorter: textSorter('t_name'),
    }),
    {
      title: '割当済み',
      key: 'question_assigned',
      width: 140,
      render: (text, record) => {
        const code = String(record.survey_code);
        const qsLen =
          record.question_set && record.question_set.length
            ? record.question_set.length
            : 0;

        // 2個必要なサーベイ（40010, 60020）の特別処理
        if (NEEDS_TWO_QUESTION_SETS.includes(code)) {
          if (qsLen === 2) {
            return (
              <Button size="small" onClick={() => showQuestionModal(record)}>
                設問確認
              </Button>
            );
          }
          if (qsLen >= 3) {
            return (
              <Button
                size="small"
                onClick={() => showQuestionModal(record)}
                style={{
                  color: '#fff',
                  backgroundColor: 'var(--blue-default)',
                  borderColor: 'var(--blue-default)',
                }}
              >
                設問超過：{qsLen - 2}
              </Button>
            );
          }
          // 0個または1個 → 割当不足
          return (
            <Button
              size="small"
              disabled
              style={{
                color: '#fff',
                backgroundColor: 'var(--red-default)',
                borderColor: 'var(--red-default)',
                opacity: 1,
              }}
            >
              必要SET：{2 - qsLen}
            </Button>
          );
        }

        // 通常のQuestionSet割当ありサーベイ
        if (qsLen > 0) {
          return (
            <Button size="small" onClick={() => showQuestionModal(record)}>
              設問確認
            </Button>
          );
        }
        // 個別Question直接割当タイプ
        if (DIRECT_QUESTION_CODES.includes(code)) {
          return (
            <Button size="small" onClick={() => showQuestionModal(record)}>
              設問確認
            </Button>
          );
        }
        // QuestionSet必須だが未割当（20110, 20210）
        if (NEEDS_QUESTION_SET_CODES.includes(code)) {
          return (
            <Button
              size="small"
              disabled
              style={{
                color: '#fff',
                backgroundColor: 'var(--red-default)',
                borderColor: 'var(--red-default)',
                opacity: 1,
              }}
            >
              必要SET：1
            </Button>
          );
        }
        return null;
      },
    },
    {
      title: '編集',
      key: 'action',
      width: 90,
      render: (text, record) => (
        <span>
          <Button
            size="small"
            onClick={(e) => editSeasonHandler(record, e)}
            disabled={false}
          >
            編集
          </Button>
        </span>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <>
      <PageContainer fluid>
      <div style={{ marginBottom: 16 }}>
        <Select
          showSearch
          allowClear
          style={{ width: 200 }}
          placeholder="企業選択"
          optionFilterProp="children"
          onChange={onChangeGroup}
          value={selGroupCode}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {groups.map((el) => (
            <Option value={el.group_code} key={el._id}>
              {el.group_name}
            </Option>
          ))}
        </Select>
      </div>

      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={filteredSeasons}
          size="middle"
          rowKey="_id"
          pagination={{
            defaultPageSize: 100,
            showSizeChanger: true,
            pageSizeOptions: ['100', '200', '500'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total}件`,
          }}
        />
      </div>

      <Button
        type="primary"
        size="small"
        onClick={(e) => newSeasonHandler(e)}
      >
        新規シーズン作成
      </Button>
      </PageContainer>

      <Modal
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span>設問確認</span>
            <Button
              size="small"
              type="primary"
              onClick={downloadExcel}
              disabled={modalLoading || Object.keys(questionData).length === 0}
            >
              設問ダウンロード
            </Button>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        {modalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : tabKeys.length === 0 ? (
          <p>設問データがありません。</p>
        ) : tabKeys.length === 1 ? (
          renderQuestionTable(questionData[tabKeys[0]])
        ) : (
          <Tabs
            items={tabKeys.map((key) => ({
              key,
              label: key,
              children: renderQuestionTable(questionData[key]),
            }))}
          />
        )}
      </Modal>
    </>
  );
};

export default SeasonListPage;
